import React from "react";
import {
  formatRelativeTime,
  formatToLocalDate,
  stripInlineColors,
} from "../../utils";
import CommentNotification from "./commentNotification";
import VerifiedBadge from "../../components/common/VerifiedBadge/VerifiedBadge";
import {
  PostDetailBadgeCheckIcon,
  PostDetailHeartIcon,
  PostDetailReplyIcon,
  PostDetailTrashIcon,
} from "../PostDetail/PostDetailCommentIcons";

const getInitials = (name) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const getLikeLabel = (comment) => {
  const total = comment.likes?.total || 0;
  if (comment.likes?.likedByCurrentUser) {
    const others = total - 1;
    return others > 0 ? `You + ${others}` : "Liked";
  }
  return String(total);
};

const formatCommentTime = (createdAt, layout) => {
  if (layout === "postDetail") {
    const relative = formatRelativeTime(createdAt);
    if (relative) return relative;
  }
  return formatToLocalDate(createdAt);
};

const CommentAvatar = ({ user, isPublisher, compact }) => {
  const avatar = user?.avatar?.small;
  const sizeClass = compact
    ? "comment-row__avatar comment-row__avatar--sm"
    : "comment-row__avatar";

  return (
    <div className={sizeClass}>
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className={isPublisher ? "comment-row__avatar-img--analyst" : undefined}
        />
      ) : (
        <span className="comment-row__avatar-initials">{getInitials(user?.name)}</span>
      )}
      {isPublisher && (
        <PostDetailBadgeCheckIcon className="comment-row__avatar-badge" />
      )}
    </div>
  );
};

const CommentAuthor = ({
  user,
  createdAt,
  isPublisher,
  replyTo,
  compact,
  layout = "legacy",
}) => {
  const avatar = user?.avatar?.small;

  if (layout === "postDetail") {
    return (
      <CommentAvatar user={user} isPublisher={isPublisher} compact={compact} />
    );
  }

  return (
    <div className={`comment-author${compact ? " comment-author--compact" : ""}`}>
      <div className="comment-author__avatar">
        {avatar ? (
          <img src={avatar} alt="" />
        ) : (
          <span className="comment-author__initials">{getInitials(user?.name)}</span>
        )}
      </div>
      <div className="comment-author__meta">
        <div className="comment-author__name-row">
          <span
            className={`comment-author__name${
              isPublisher && !compact ? " comment-author__name--analyst" : ""
            }`}
          >
            {user?.name}
          </span>
          {isPublisher && (
            <>
              <span className="comment-author__badge">Analyst</span>
              <VerifiedBadge className="comment-author__verified" />
            </>
          )}
          {replyTo ? (
            <span className="comment-author__reply-chip">Replying to {replyTo}</span>
          ) : null}
        </div>
        <time className="comment-author__date" dateTime={createdAt}>
          {formatCommentTime(createdAt, layout)}
        </time>
      </div>
    </div>
  );
};

const CommentBlock = ({
  comment,
  user,
  canReply,
  isReplyingDisabled,
  onDelete,
  onLike,
  onReply,
  showNotify,
  replyTo,
  compact,
  layout = "legacy",
}) => {
  const isPublisher = comment.user?.roleId === 3;
  const likeTotal = comment.likes?.total || 0;
  const likedByCurrentUser = !!comment.likes?.likedByCurrentUser;
  const canDelete =
    user &&
    (user.userRoleId === 1 ||
      user.userRoleId === 3 ||
      (comment.user && user.id === comment.user.id));

  const commentHtml = (
    <div
      className="comment-text"
      dangerouslySetInnerHTML={{
        __html: stripInlineColors(comment.content),
      }}
    />
  );

  const actionButtons = (
    <>
      <button
        type="button"
        className={`comment-action comment-action--like${
          likedByCurrentUser ? " is-active" : ""
        }`}
        onClick={() => onLike(comment)}
        aria-label="Like comment"
      >
        {layout === "postDetail" ? (
          <PostDetailHeartIcon filled={likedByCurrentUser} />
        ) : (
          <span className="material-icons" aria-hidden="true">
            thumb_up
          </span>
        )}
        <span>
          {layout === "postDetail" ? likeTotal : getLikeLabel(comment)}
        </span>
      </button>

      {canReply && (
        <button
          type="button"
          className={`comment-action comment-action--reply${
            comment.replyOpen ? " is-open" : ""
          }`}
          disabled={isReplyingDisabled}
          onClick={() => onReply(comment.id)}
        >
          {layout === "postDetail" ? (
            <PostDetailReplyIcon />
          ) : (
            <span className="material-icons" aria-hidden="true">
              reply
            </span>
          )}
          <span>{comment.replyOpen ? "Cancel" : "Reply"}</span>
        </button>
      )}

      {canDelete && layout === "postDetail" && (
        <button
          type="button"
          className="comment-action comment-action--delete"
          aria-label="Delete comment"
          onClick={() => onDelete(comment)}
        >
          <PostDetailTrashIcon />
          <span>Delete</span>
        </button>
      )}
    </>
  );

  if (layout === "postDetail") {
    return (
      <div
        className={`comment-row${isPublisher ? " comment-row--analyst" : ""}${
          compact ? " comment-row--compact" : ""
        }`}
      >
        <CommentAuthor
          user={comment.user}
          createdAt={comment.createdAt}
          isPublisher={isPublisher}
          replyTo={replyTo}
          compact={compact}
          layout={layout}
        />
        <div className="comment-row__main">
          <div
            className={`comment-bubble${
              isPublisher ? " comment-bubble--analyst" : ""
            }`}
          >
            <div className="comment-bubble__meta">
              <span className="comment-bubble__name">{comment.user?.name}</span>
              {isPublisher && (
                <span className="comment-author__badge">Analyst</span>
              )}
              <span className="comment-bubble__sep" aria-hidden="true">
                ·
              </span>
              <time dateTime={comment.createdAt}>
                {formatCommentTime(comment.createdAt, layout)}
              </time>
            </div>
            {commentHtml}
          </div>

          <div className="comment-row__actions">
            {actionButtons}
            {showNotify && (
              <CommentNotification
                comment={comment}
                commentId={comment.id}
                currentUser={user}
                variant="postDetail"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="comment-card__head">
        <CommentAuthor
          user={comment.user}
          createdAt={comment.createdAt}
          isPublisher={isPublisher}
          replyTo={replyTo}
          compact={compact}
          layout={layout}
        />
        {canDelete ? (
          <button
            type="button"
            className="comment-card__delete"
            aria-label="Delete comment"
            onClick={() => onDelete(comment)}
          >
            <span className="material-icons" aria-hidden="true">
              delete_outline
            </span>
          </button>
        ) : null}
      </header>

      <div className="comment-card__body">
        {commentHtml}

        <div className="comment-footer">
          <div className="comment-actions">{actionButtons}</div>

          {showNotify && (
            <div className="comment-notify-inline">
              <CommentNotification
                comment={comment}
                commentId={comment.id}
                currentUser={user}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommentBlock;
